import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  Alert,
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Progress,
  Spinner,
} from "reactstrap";

import ClientPicker from "./ClientPicker";
import {
  ClientRecord,
  copyRecordToService,
  getRecordsByClient,
} from "../../services/clientRecordService";
import { getServices, Service } from "../../services/serviceService";

type CopyAnamnesisToServicesModalProps = {
  isOpen: boolean;
  onClose: () => void;
  /** Pre-selected client (skip picker when set). */
  clientId?: number | null;
  clientName?: string | null;
  /** Pre-selected general record (skip record list when set). */
  sourceRecord?: ClientRecord | null;
  onCopied?: () => void;
};

const CopyAnamnesisToServicesModal = ({
  isOpen,
  onClose,
  clientId: lockedClientId = null,
  clientName: lockedClientName = null,
  sourceRecord: lockedRecord = null,
  onCopied,
}: CopyAnamnesisToServicesModalProps) => {
  const { t } = useTranslation();

  const [clientId, setClientId] = useState("");
  const [generalRecords, setGeneralRecords] = useState<ClientRecord[]>([]);
  const [selectedRecordId, setSelectedRecordId] = useState("");
  const [services, setServices] = useState<Service[]>([]);
  const [selectedServiceIds, setSelectedServiceIds] = useState<number[]>([]);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [loadingServices, setLoadingServices] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [progressCurrent, setProgressCurrent] = useState(0);
  const [progressTotal, setProgressTotal] = useState(0);
  const cancelRequestedRef = useRef(false);

  const resolvedClientId = lockedClientId ?? (clientId ? Number(clientId) : null);
  const resolvedRecord =
    lockedRecord ??
    generalRecords.find((record) => record.id === Number(selectedRecordId)) ??
    null;

  const reset = useCallback(() => {
    setClientId(lockedClientId ? String(lockedClientId) : "");
    setGeneralRecords([]);
    setSelectedRecordId(lockedRecord ? String(lockedRecord.id) : "");
    setSelectedServiceIds([]);
    setSubmitting(false);
    setProgressCurrent(0);
    setProgressTotal(0);
    cancelRequestedRef.current = false;
  }, [lockedClientId, lockedRecord]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    reset();
  }, [isOpen, reset]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    let cancelled = false;

    const loadServices = async () => {
      setLoadingServices(true);

      try {
        const response = await getServices({ per_page: 100 });
        if (!cancelled) {
          setServices(response.data ?? []);
        }
      } catch (error: any) {
        if (!cancelled) {
          toast.error(error?.data || t("clients.anamnesis.copy.toast.loadServicesError"));
          setServices([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingServices(false);
        }
      }
    };

    void loadServices();

    return () => {
      cancelled = true;
    };
  }, [isOpen, t]);

  useEffect(() => {
    if (!isOpen || lockedRecord || !resolvedClientId) {
      if (!lockedRecord) {
        setGeneralRecords([]);
        setSelectedRecordId("");
      }

      return;
    }

    let cancelled = false;

    const loadRecords = async () => {
      setLoadingRecords(true);

      try {
        const response = await getRecordsByClient(resolvedClientId, {
          per_page: 50,
          general_only: true,
        });

        if (cancelled) {
          return;
        }

        setGeneralRecords(response.data ?? []);
        setSelectedRecordId((current) => {
          if (current && response.data.some((record) => String(record.id) === current)) {
            return current;
          }

          return response.data[0] ? String(response.data[0].id) : "";
        });
      } catch (error: any) {
        if (!cancelled) {
          toast.error(error?.data || t("clients.anamnesis.toast.loadRecordsError"));
          setGeneralRecords([]);
          setSelectedRecordId("");
        }
      } finally {
        if (!cancelled) {
          setLoadingRecords(false);
        }
      }
    };

    void loadRecords();

    return () => {
      cancelled = true;
    };
  }, [isOpen, lockedRecord, resolvedClientId, t]);

  const toggleService = (serviceId: number) => {
    if (submitting) {
      return;
    }

    setSelectedServiceIds((current) =>
      current.includes(serviceId)
        ? current.filter((id) => id !== serviceId)
        : [...current, serviceId]
    );
  };

  const requestCancelCopy = () => {
    cancelRequestedRef.current = true;
  };

  const handleClose = () => {
    if (submitting) {
      cancelRequestedRef.current = true;
      return;
    }

    onClose();
  };

  const handleSubmit = async () => {
    if (!resolvedClientId || !resolvedRecord) {
      toast.error(t("clients.anamnesis.copy.validation.recordRequired"));
      return;
    }

    if (selectedServiceIds.length === 0) {
      toast.error(t("clients.anamnesis.copy.validation.servicesRequired"));
      return;
    }

    setSubmitting(true);
    cancelRequestedRef.current = false;
    setProgressCurrent(0);
    setProgressTotal(selectedServiceIds.length);

    let successCount = 0;
    let skipCount = 0;
    let errorCount = 0;
    let cancelledMidway = false;

    for (let index = 0; index < selectedServiceIds.length; index += 1) {
      if (cancelRequestedRef.current) {
        cancelledMidway = true;
        break;
      }

      const serviceId = selectedServiceIds[index];

      try {
        await copyRecordToService(resolvedClientId, resolvedRecord.id, {
          service_id: serviceId,
        });
        successCount += 1;
      } catch (error: any) {
        if (error?.code === "CLINICAL_RECORD_DUPLICATE" || error?.status === 422) {
          skipCount += 1;
        } else {
          errorCount += 1;
        }
      }

      setProgressCurrent(index + 1);
    }

    setSubmitting(false);
    cancelRequestedRef.current = false;

    if (successCount > 0) {
      toast.success(
        t("clients.anamnesis.copy.toast.success", {
          count: successCount,
        })
      );
      onCopied?.();
    }

    if (skipCount > 0) {
      toast.info(
        t("clients.anamnesis.copy.toast.skipped", {
          count: skipCount,
        })
      );
    }

    if (errorCount > 0) {
      toast.error(
        t("clients.anamnesis.copy.toast.partialError", {
          count: errorCount,
        })
      );
    }

    if (cancelledMidway) {
      toast.info(
        t("clients.anamnesis.copy.toast.cancelled", {
          done: successCount + skipCount + errorCount,
          total: selectedServiceIds.length,
        })
      );
    }

    if (successCount > 0 && errorCount === 0 && !cancelledMidway) {
      onClose();
    }
  };

  const titleName = lockedClientName || undefined;
  const progressPercent =
    progressTotal > 0 ? Math.round((progressCurrent / progressTotal) * 100) : 0;

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered scrollable>
      <ModalHeader toggle={handleClose}>
        {titleName
          ? t("clients.anamnesis.copy.titleWithClient", { name: titleName })
          : t("clients.anamnesis.copy.title")}
      </ModalHeader>
      <ModalBody>
        <Alert color="info" className="py-2">
          <p className="mb-0 small">{t("clients.anamnesis.copy.hint")}</p>
        </Alert>

        {!lockedClientId ? (
          <FormGroup className="mb-3">
            <Label htmlFor="copy-anamnesis-client">{t("clients.anamnesis.copy.fields.client")}</Label>
            <ClientPicker
              id="copy-anamnesis-client"
              value={clientId}
              onChange={setClientId}
              isDisabled={submitting}
            />
          </FormGroup>
        ) : null}

        {!lockedRecord ? (
          <FormGroup className="mb-3">
            <Label htmlFor="copy-anamnesis-record">{t("clients.anamnesis.copy.fields.record")}</Label>
            {loadingRecords ? (
              <div className="py-2">
                <Spinner size="sm" />
              </div>
            ) : !resolvedClientId ? (
              <p className="text-muted small mb-0">{t("clients.anamnesis.copy.pickClientFirst")}</p>
            ) : generalRecords.length === 0 ? (
              <p className="text-muted small mb-0">{t("clients.anamnesis.copy.emptyGeneral")}</p>
            ) : (
              <Input
                id="copy-anamnesis-record"
                type="select"
                value={selectedRecordId}
                disabled={submitting}
                onChange={(event) => setSelectedRecordId(event.target.value)}
              >
                {generalRecords.map((record) => (
                  <option key={record.id} value={record.id}>
                    {record.template?.title ?? t("clients.anamnesis.common.notAvailable")}
                    {" · #"}
                    {record.id}
                  </option>
                ))}
              </Input>
            )}
          </FormGroup>
        ) : (
          <p className="mb-3 small">
            <span className="fw-medium">{t("clients.anamnesis.copy.fields.record")}: </span>
            {lockedRecord.template?.title ?? t("clients.anamnesis.common.notAvailable")}
          </p>
        )}

        <FormGroup className="mb-0">
          <Label className="d-block">{t("clients.anamnesis.copy.fields.services")}</Label>
          {loadingServices ? (
            <Spinner size="sm" />
          ) : services.length === 0 ? (
            <p className="text-muted small mb-0">{t("clients.anamnesis.copy.emptyServices")}</p>
          ) : (
            <div className="border rounded p-2" style={{ maxHeight: 220, overflowY: "auto" }}>
              {services.map((service) => (
                <div className="form-check mb-1" key={service.id}>
                  <Input
                    id={`copy-anamnesis-service-${service.id}`}
                    type="checkbox"
                    className="form-check-input"
                    checked={selectedServiceIds.includes(service.id)}
                    disabled={submitting}
                    onChange={() => toggleService(service.id)}
                  />
                  <Label
                    className="form-check-label"
                    htmlFor={`copy-anamnesis-service-${service.id}`}
                  >
                    {service.name}
                  </Label>
                </div>
              ))}
            </div>
          )}
          <p className="text-muted small mb-0 mt-1">
            {t("clients.anamnesis.copy.fields.servicesHint")}
          </p>
        </FormGroup>

        {submitting ? (
          <div className="mt-3">
            <div className="d-flex justify-content-between small mb-1">
              <span>{t("clients.anamnesis.copy.progressLabel")}</span>
              <span>
                {t("clients.anamnesis.copy.progressCount", {
                  current: progressCurrent,
                  total: progressTotal,
                })}
              </span>
            </div>
            <Progress value={progressPercent} className="mb-2" />
            <Button color="soft-danger" size="sm" type="button" onClick={requestCancelCopy}>
              {t("clients.anamnesis.copy.actions.cancelCopy")}
            </Button>
          </div>
        ) : null}
      </ModalBody>
      <ModalFooter>
        <Button color="light" type="button" onClick={handleClose} disabled={submitting}>
          {t("clients.anamnesis.actions.close")}
        </Button>
        <Button
          color="primary"
          type="button"
          disabled={
            submitting ||
            !resolvedRecord ||
            selectedServiceIds.length === 0 ||
            loadingServices
          }
          onClick={() => {
            void handleSubmit();
          }}
        >
          {submitting ? <Spinner size="sm" className="me-1" /> : null}
          {t("clients.anamnesis.copy.actions.submit", {
            count: selectedServiceIds.length || 0,
          })}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default CopyAnamnesisToServicesModal;
